import './more-page.scss';
import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import {
	getCategoryPages,
	getGenrePages,
	getPosterPages,
	getTagMorePages,
} from '../../shared/services/more-page/more-page-service';
import Loading from '../../components/loading/item-loading/loading';
import ListView from '../../components/list-view/live-view';
import BaseEntity from '../../components/base-entity/base-entity';
import ImageGenerator from '../../components/image-generator/image-generator';
import { Button } from '@material-ui/core';
import { setTitle } from '../../shared/services/general/general-service';
import { useConfigApp } from '../../shared/services/general/app-context';
import { Helmet } from 'react-helmet';
import Breadcrumb from '../../components/breadcrumb/breadcrumb';
import { useLocation } from 'react-router-dom';
import { useSeo } from '../../shared/services/general/seoManager';
import SkeletonLoader from '../../components/skeleton-loader/skeleton-loader';

const PAGE_SIZE = 200;
const TAG_PAGE_SIZE = 199;
const LOADING_DELAY = 500;
const DESKTOP_BREAKPOINT = 980;

const getPathType = (path) => {
	if (path.includes('/category/')) return 'category';
	else if (path.includes('/genre/')) return 'genre';
	else if (path.includes('/poster/')) return 'poster';
	else return 'tag';
};

const isArrayType = (pathType) => pathType !== 'tag';

function TagMorePage(props) {
	const [data, setData] = useState([]);
	const [tagIsArray, setTagIsArray] = useState(null);
	const [pageSeoData, setPageSeoData] = useState(null);
	const [loading, setLoading] = useState(true);
	const [page, setPage] = useState(0);
	const [showLoadMore, setShowLoadMore] = useState(false);
	const [clearData, setClearData] = useState(true);
	const [isDesktop, setIsDesktop] = useState(window.innerWidth > DESKTOP_BREAKPOINT);

	const configApp = useConfigApp();
	const location = useLocation();
	const { setSeoData } = useSeo();

	const prevPage = useRef(null);
	const prevId = useRef(null);

	const path = location.pathname;
	const pathType = useMemo(() => getPathType(path), [path]);
	const { id, type } = props.match.params;

	useEffect(() => {
		const handleResize = () => {
			setIsDesktop(window.innerWidth > DESKTOP_BREAKPOINT);
		};
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	const handleApiResponse = useCallback((promise, options = {}) => {
		return promise
			.then((res) => {
				const { onSuccess } = options;
				if (onSuccess) onSuccess(res);
				setLoading(false);
			})
			.catch((error) => {
				console.error('API Error:', error);
				setLoading(false);
			})
			.finally(() => {
				setTimeout(() => setLoading(false), LOADING_DELAY);
			});
	}, []);

	const getTagMorePagesApi = useCallback(() => {
		handleApiResponse(
			getTagMorePages(id, type, page),
			{
				onSuccess: (res) => {
					const responseType = Array.isArray(res.data.data);
					setTagIsArray(responseType);

					if (responseType) {
						const newData = res.data.data;
						setShowLoadMore(newData.length >= TAG_PAGE_SIZE);
						setClearData(true);
						setData((prevData) => [...prevData, ...newData]);
					} else {
						setShowLoadMore(false);
						setData(res.data.data);
						setPageSeoData({
							title: `${configApp?.title} | ${res.data.data.value}`,
							image: res.data.data?.fields?.image,
							description: null,
						});
					}
				}
			}
		);
	}, [id, type, page, configApp?.title, handleApiResponse]);

	const getCategoryPagesApi = useCallback(() => {
		prevPage.current = page;
		prevId.current = id;

		handleApiResponse(
			getCategoryPages(id, type, page, PAGE_SIZE),
			{
				onSuccess: (res) => {
					if (res.data.code === 200) {
						const newData = res.data.data;
						setShowLoadMore(newData?.totalPages > page + 1);
						setClearData(true);

						if (page === 0) {
							setData({
								category: newData.category,
								totalPages: newData.totalPages,
								totalContentCount: newData.totalContentCount,
								contents: newData.contents,
								slider: newData.slider,
							});
							setPageSeoData({
								title: newData?.category?.name,
								image: newData?.category?.image,
								description: newData?.category?.description || null,
							});
						} else {
							setData((prevData) => ({
								...prevData,
								contents: [...(prevData.contents || []), ...newData.contents],
							}));
						}
					}
				}
			}
		);
	}, [id, type, page, handleApiResponse]);

	const getGenrePagesApi = useCallback(() => {
		prevPage.current = page;
		prevId.current = id;

		handleApiResponse(
			getGenrePages(id, type, page, PAGE_SIZE),
			{
				onSuccess: (res) => {
					if (res.data.code === 200) {
						const newData = res.data.data;
						setShowLoadMore(newData?.totalPages > page + 1);
						setClearData(true);

						if (page === 0) {
							setData({
								genre: newData.genre,
								totalPages: newData.totalPages,
								totalContentCount: newData.totalContentCount,
								contents: newData.contents,
							});
							setPageSeoData({
								title: newData?.genre?.name,
								image: newData?.genre?.image,
								description: newData?.genre?.description,
							});
						} else {
							setData((prevData) => ({
								...prevData,
								contents: [...(prevData.contents || []), ...newData.contents],
							}));
						}
					}
				}
			}
		);
	}, [id, type, page, handleApiResponse]);

	const getPosterPagesApi = useCallback(() => {
		prevPage.current = page;
		prevId.current = id;

		handleApiResponse(
			getPosterPages(id, type, page, PAGE_SIZE),
			{
				onSuccess: (res) => {
					if (res.data.code === 200) {
						const newData = res.data.data;
						setShowLoadMore(newData?.totalPages > page + 1);
						setClearData(true);

						if (page === 0) {
							getTagMorePages(id, type, page).then((tagRes) => {
								if (tagRes.data.data) {
									const titlePage = tagRes.data.data?.value;
									setData((prevData) => ({
										...prevData,
										titlePages: titlePage,
									}));
									setPageSeoData({
										title: titlePage,
										image: tagRes.data.data?.fields.image,
										description: null,
									});
								}
							});

							setData({
								titlePages: '...',
								totalPages: newData.totalPages,
								totalContentCount: newData.totalContentCount,
								contents: newData.contents,
							});
						} else {
							setData((prevData) => ({
								...prevData,
								contents: [...(prevData.contents || []), ...newData.contents],
							}));
						}
					}
				}
			}
		);
	}, [id, type, page, handleApiResponse]);

	const apiFunctions = useMemo(() => ({
		category: getCategoryPagesApi,
		genre: getGenrePagesApi,
		poster: getPosterPagesApi,
		tag: getTagMorePagesApi,
	}), [getCategoryPagesApi, getGenrePagesApi, getPosterPagesApi, getTagMorePagesApi]);

	useEffect(() => {
		const isArrayPath = isArrayType(pathType);
		setTagIsArray(isArrayPath);
		apiFunctions[pathType]();
	}, [pathType, apiFunctions]);

	useEffect(() => {
		if (clearData && page === 0) {
			setData([]);
			setLoading(true);
		}

		const shouldRefetch = isArrayType(pathType) &&
			prevPage.current !== null &&
			prevId.current !== null &&
			(prevPage.current !== page || prevId.current !== id);

		if (shouldRefetch || pathType === 'tag') {
			apiFunctions[pathType]();
		}
	}, [page, path, pathType, id, clearData, apiFunctions]);

	useEffect(() => {
		setData([]);
		setLoading(true);
		setPage(0);
	}, [path]);

	useEffect(() => {
		if (data?.value && !tagIsArray) {
			setPageSeoData({
				title: data.value,
				image: data?.fields?.image,
				description: null,
			});
		}
	}, [data, tagIsArray]);

	useEffect(() => {
		if (pageSeoData?.title && configApp) {
			const title = `${pageSeoData.title} | ${configApp.title}`;
			setSeoData({ title });
			setTitle(title);
		} else if (configApp?.title) {
			setTitle(configApp.title);
		}
	}, [pageSeoData, configApp, setSeoData]);

	const handleLoadMore = useCallback(() => {
		setPage(prev => prev + 1);
		setClearData(false);
	}, []);

	const renderSkeleton = () => (
		<div className="tag-page" style={{ padding: '0 40px' }}>
			<div className="skeleton-loader-items" style={{ paddingRight: '10px', paddingBottom: '30px' }}>
				<div className="d-flex" style={{ gap: '20px', marginTop: '88px', marginBottom: '20px' }}>
					<SkeletonLoader type="text" count={1} width="70px" height="12px" />
					<SkeletonLoader type="text" count={1} width="70px" height="12px" />
					<SkeletonLoader type="text" count={1} width="70px" height="12px" />
				</div>
				<SkeletonLoader type="text" count={1} width="70px" height="20px" />
			</div>

			{pathType === 'category' && (
				isDesktop ?
					<SkeletonLoader type="big-slider" borderRadius={16} count={4} /> :
					<SkeletonLoader type="rectangle" count={1} width="100vw" height="130vw" borderRadius="16px" />
			)}

			<div style={{ padding: '0 18px' }}>
				<SkeletonLoader type="items" count={49} />
			</div>
		</div>
	);

	const renderEmptyState = () => (
		<p style={{ color: 'white', fontSize: '16px', textAlign: 'center', marginTop: '100px' }}>
			متاسفانه محتوایی برای نمایش وجود ندارد
		</p>
	);

	const renderTagObject = () => (
		<div>
			{configApp?.titleEn !== 'bebin' && (
				<Helmet>
					<meta name="robots" content="noindex, follow" />
				</Helmet>
			)}
			<div className="warp">
				<Breadcrumb
					style={{ marginBottom: '-4px' }}
					firstItem={{ label: 'خانه', path: '/' }}
					currentItem={data?.value}
				/>
				<div className="tag-info">
					<div className="tag-header">
						<div
							className="tag-background-image"
							style={{ backgroundImage: `url(${data?.fields?.image})` }}
						/>
						<div className="tag-info-box">
							{!['genre', 'category'].includes(data?.tagDefinitionName) && (
								<div className="tag-image">
									<ImageGenerator src={data?.fields?.image} name={data?.value} />
								</div>
							)}
						</div>
					</div>
					<div className="tag-body">
						<h1>{data?.value}</h1>
						{data?.fields?.bio && (
							<>
								<br />
								<p>{data.fields.bio}</p>
							</>
						)}
					</div>
				</div>
			</div>
			<BaseEntity data={data?.carousels} noFooter />
		</div>
	);

	if (loading) {
		return renderSkeleton();
	}

	if (!data || data?.length === 0 || data?.contents?.length === 0) {
		return renderEmptyState();
	}

	return (
		<div className={`list-view-container${data?.tagDefinitionName ? ' ' + data.tagDefinitionName : ''}`}>
			{tagIsArray ? (
				<ListView data={data} type={type} />
			) : (
				renderTagObject()
			)}

			{showLoadMore && (
				<Button
					onClick={handleLoadMore}
					hidden={page === 0 && loading}
					disabled={loading}
					variant="contained"
					className="load-more"
				>
					<span>موارد بیشتر</span>
				</Button>
			)}
		</div>
	);
}

export default TagMorePage;